import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useShakePagination } from "@common/hooks/useShakePaginaton";
import { ReleaseVersion } from "@common/models/Analytics.model";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useAnalyticsDeps } from "@main/pages/Release/Release";

const SEARCH_DEBOUNCE_MS = 300;

function mapToVersions(items: ReleaseVersion[]) {
  return items.map((r) => r.version);
}

export default function useReleaseVersionsConsumer() {
  const { analyticsService } = useAnalyticsDeps();
  const { selectedWorkspace, selectedApp } = useAppSelectionContext();

  const [releaseSearch, setReleaseSearchState] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const setReleaseSearch = useCallback((value: string) => {
    setReleaseSearchState(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (value === "") {
      setDebouncedSearch("");
      return;
    }

    debounceRef.current = setTimeout(() => setDebouncedSearch(value), SEARCH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setReleaseSearchState("");
    setDebouncedSearch("");
  }, [selectedApp?.id]);

  const select = useMemo(() => ({ mapperDeps: [], mapper: mapToVersions }), []);

  const pagination = useShakePagination<ReleaseVersion, string>({
    deps: [selectedWorkspace?.id, selectedApp?.id, debouncedSearch],
    fetchFn: ({ deps, pagination, signal }) => {
      const [workspaceId, appId, search] = deps as [string, string, string];

      return analyticsService
        .getReleases(workspaceId, appId, { search, offset: pagination.offset, limit: pagination.limit }, signal)
        .then(({ data }) => ({ total: data.total, items: data.items }));
    },
    enabled: (deps) => Boolean(deps[0]) && Boolean(deps[1]),
    select,
    queryKey: `releases ${selectedApp?.id}`,
    disableAutoPrefetchNextPage: true,
  });

  const releaseVersions = pagination.mappedData ?? [];

  const ensureVersionKnown = async (version: string): Promise<boolean> => {
    if (releaseVersions.includes(version)) return true;
    if (!selectedWorkspace?.id || !selectedApp?.id) return false;

    const limit = 100;
    let offset = 0;

    while (true) {
      const { data } = await analyticsService.getReleases(selectedWorkspace.id, selectedApp.id, {
        search: version,
        offset,
        limit,
      });

      if (data.items.some((item) => item.version === version)) return true;

      offset += data.items.length;
      if (offset >= data.total || data.items.length < limit) return false;
    }
  };

  return {
    releaseVersions,
    isLoading: pagination.isLoading,
    isFetchingNextPage: pagination.isFetchingNextPage,
    hasMore: pagination.hasMore,
    loadNext: pagination.loadNext,
    error: pagination.error,
    releaseSearch,
    setReleaseSearch,
    ensureVersionKnown,
  };
}
